import React, { useContext, useEffect, useState } from 'react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import type { WebsocketSession } from 'src/api-sdk';
import { AlertsApi } from 'src/api-sdk';

import { useAPI } from './use-api';
import { useAuth } from './use-auth';
import { QUERIES } from './globals';
import { ReconnectingWebsocket } from './reconnecting-websocket';

interface WebsocketContextInterface {
  connected: boolean;
  session: WebsocketSession | undefined;
  websocket: WebSocket | undefined;
  sendToWebsocket: (data: any) => any;
  registerListener: (listener: WebsocketListener) => any;
  removeListener: (listener: WebsocketListener) => any;
}

export interface WebsocketListener {
  key: string;
  message_type: WebsocketMessageType;
  callback: (message: WebsocketMessage) => any;
}

export enum WebsocketMessageType {
  REGISTRATION_UPDATE = 'REGISTRATION_UPDATE',
  REGISTRATION_COMPANY_UPDATE = 'REGISTRATION_COMPANY_UPDATE',
  REGISTRATION_ARTICLE_UPDATE = 'REGISTRATION_ARTICLE_UPDATE',
  NEW_ARTICLE_PUBLISHED = 'NEW_ARTICLE_PUBLISHED',
  BINDER_SUMMARY_UPDATE = 'BINDER_SUMMARY_UPDATE',
  ARTICLE_SUMMARY_UPDATE = 'ARTICLE_SUMMARY_UPDATE',
  ARTICLE_EXPORT_AVAILABLE = 'ARTICLE_EXPORT_AVAILABLE',
  EXPORT_AVAILABLE = 'EXPORT_AVAILABLE',
  BATCH_REGISTRATION_STATUS_UPDATE = 'BATCH_REGISTRATION_STATUS_UPDATE',
  NOTIFICATION_UPDATE = 'NOTIFICATION_UPDATE',
  SCREENING_STATUS_UPDATE = 'SCREENING_STATUS_UPDATE',
  CHAT_EVENT = 'CHAT_EVENT',
}

export interface WebsocketMessage {
  message_type: WebsocketMessageType;
  data: any;
}

const websocketContext = React.createContext<WebsocketContextInterface>(
  {} as ReturnType<typeof useProvideWebsocket>
);

export function ProvideWebsocket({ children }: any): any {
  const websocket = useProvideWebsocket();
  return (
    <websocketContext.Provider value={websocket}>
      {children}
    </websocketContext.Provider>
  );
}

export const useWebsocket = () => {
  return useContext(websocketContext);
};

// Provider hook that creates auth object and handles state
function useProvideWebsocket(): WebsocketContextInterface {
  const { api, authHeaderSet, configuration } = useAPI();
  const { user } = useAuth();
  const [connected, setConnected] = useState<boolean>(false);
  const [listeners, setListeners] = useState<WebsocketListener[]>([]);
  const [websocket, setWebsocket] = useState<WebSocket | undefined>();

  const baseWebsocketUrl = process.env.NEXT_PUBLIC_API_WEBSOCKETS_URL as string;

  const enabled = Boolean(authHeaderSet && user);

  const session = useQuery({
    queryKey: [QUERIES.WEBSOCKET_SESSION],

    queryFn: AwesomeDebouncePromise(async () => {
      return await getWebsocketSession();
    }, 300),

    enabled,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  /**
   * Get new Websocket Session.
   */
  const getWebsocketSession = React.useCallback(() => {
    return new Promise<WebsocketSession>((resolve, reject) => {
      new AlertsApi(configuration, undefined, api)
        .alertsWebsocketsSessionsCreate()
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }, [api, configuration]);

  const listenersRef = React.useRef(listeners);
  useEffect(() => {
    listenersRef.current = listeners;
  }, [listeners]);

  const sessionRef = React.useRef(session.data);
  useEffect(() => {
    sessionRef.current = session.data;
  }, [session.data]);

  const onWebsocketMessage = React.useCallback((e: { data: string }) => {
    const data: { message_type: string } = JSON.parse(e.data);
    const message: WebsocketMessage = {
      // @ts-ignore
      message_type: WebsocketMessageType[data.message_type],
      data: {
        ...data,
        message_type: undefined,
      },
    };

    console.log('Websocket message received', message.data);

    const currentListeners = listenersRef.current;
    console.debug(
      `Websocket message received with ${currentListeners.length} listeners`,
      message
    );

    for (const listener of currentListeners) {
      if (listener.message_type !== message.message_type) {
        continue;
      }

      try {
        listener.callback(message);
      } catch (e) {
        console.log(`Callback of listener ${listener} failed due to ${e}`);
      }
    }
  }, []);

  const registerListener = React.useCallback(
    (listener: WebsocketListener) => {
      // console.debug("Registering listener", listener)
      setListeners((prevState: WebsocketListener[]) => {
        prevState = prevState.filter((value) => value.key !== listener.key);
        return [...prevState, listener];
      });
    },
    [setListeners]
  );

  const removeListener = React.useCallback(
    (listener: WebsocketListener) => {
      // console.debug("Removing listener", listener)
      setListeners((prevState: WebsocketListener[]) => {
        prevState = prevState.filter((value) => value.key !== listener.key);
        return [...prevState];
      });
    },
    [setListeners]
  );

  const controllerRef = React.useRef<ReconnectingWebsocket | null>(null);

  // Close the socket intentionally on unmount.
  useEffect(() => {
    return () => {
      controllerRef.current?.close();
      controllerRef.current = null;
    };
  }, []);

  // Close the socket intentionally on logout / loss of session.
  useEffect(() => {
    if (!enabled) {
      controllerRef.current?.close();
      controllerRef.current = null;
      setConnected(false);
      setWebsocket(undefined);
    }
  }, [enabled]);

  // Create the connection once a session is available. Reconnects after
  // abnormal closes are handled inside ReconnectingWebsocket.
  useEffect(() => {
    if (!enabled || !session.data || controllerRef.current) {
      return;
    }

    const controller = new ReconnectingWebsocket({
      getUrl: async () => {
        let data = sessionRef.current;
        if (!data || moment(data.expired_at) < moment()) {
          const result = await session.refetch();
          data = result.data;
        }
        return `${baseWebsocketUrl}?access_token=${data?.access_token}`;
      },
      createWebSocket: (url) => new WebSocket(url),
      handlers: {
        onOpen: (ws) => {
          setConnected(true);
          setWebsocket(ws);
        },
        onMessage: onWebsocketMessage,
        onClose: (e) => {
          console.log(`WebSocket is closed: ${e.code} ${e.reason}`);
          setConnected(false);
        },
      },
    });
    controllerRef.current = controller;
    controller.connect();
  }, [enabled, session.data, session, baseWebsocketUrl, onWebsocketMessage]);

  const sendToWebsocket = React.useCallback(
    (data) => {
      if (websocket && connected) {
        console.debug(`Sending update to websocket: ${JSON.stringify(data)}`);
        try {
          websocket.send(JSON.stringify(data));
        } catch (e) {
          console.error('Failed sending update to websocket');
        }
      }
    },
    [websocket, connected]
  );

  return {
    connected: connected,
    session: session.data,
    websocket,
    sendToWebsocket,
    registerListener,
    removeListener,
  };
}
